import type { ApolloError } from 'apollo-client'
import { IMPORT_MAX_COLUMNS, IMPORT_MAX_INLINE_LABEL, IMPORT_MAX_ROWS } from './importLimits'

/**
 * Error codes the import resolvers put on `UserInputError.extensions.importCode`.
 * Mirrors `ImportErrorCode` in `apps/api/src/services/import/errors.ts`.
 */
export type ImportErrorCode =
  | 'EMPTY_FILE'
  | 'NO_DATA_ROWS'
  | 'HEADER_ROW_MISSING'
  | 'MALFORMED_CSV'
  | 'TOO_MANY_ROWS'
  | 'TOO_MANY_COLUMNS'
  | 'FILE_TOO_LARGE'
  | 'FILE_KEY_INVALID'
  | 'SOURCE_REQUIRED'
  | 'MAPPING_INVALID'
  | 'REQUIRED_FIELD_UNMAPPED'
  | 'FIELD_NOT_AVAILABLE'
  | 'MISSING_FIRST_NAME'
  | 'MISSING_EMAIL'
  | 'INVALID_EMAIL'
  | 'MISSING_COMPANY_NAME'
  | 'VALUE_TOO_LONG'
  | 'DUPLICATE_IN_FILE'
  | 'DUPLICATE_SKIPPED'
  | 'NO_CHANGES'
  | 'WRITE_FAILED'
  | 'IMPORT_DISABLED'
  | 'OWNER_REQUIRED'
  | 'OWNER_INVALID'

export const GENERIC_IMPORT_ERROR = 'Something went wrong. Try again.'

/**
 * Apollo maps the server's permission/flag guard to this code; it is not an ImportErrorCode. The
 * server reads `selfServeImport` through a polling LaunchDarkly SDK, so right after the flag is
 * turned on it can still say no while the browser already shows the Import button (#24952).
 */
export const FORBIDDEN_COPY =
  "Importing isn't enabled for this account. If it was just turned on, wait a minute and try again."

/**
 * User-facing copy for every code the server can return. Server messages are developer-facing,
 * so a known code never shows the raw message.
 */
export const IMPORT_ERROR_COPY: Record<ImportErrorCode, string> = {
  EMPTY_FILE: 'That file is empty. Export your spreadsheet as CSV and try again.',
  NO_DATA_ROWS: 'That file has a header row but no data rows.',
  HEADER_ROW_MISSING:
    "Your file's first row looks like data, not column names. Add a header row (for example First Name, Email) and upload it again.",
  MALFORMED_CSV: 'That file could not be read as CSV. Re-export it from your spreadsheet and try again.',
  TOO_MANY_ROWS: `Imports are limited to ${IMPORT_MAX_ROWS.toLocaleString()} rows. Split the file and import it in parts.`,
  TOO_MANY_COLUMNS: `Imports are limited to ${IMPORT_MAX_COLUMNS} columns. Remove the columns you do not need.`,
  FILE_TOO_LARGE: `That file is over ${IMPORT_MAX_INLINE_LABEL}. Split it into smaller files.`,
  FILE_KEY_INVALID: 'That upload expired. Go back and choose the file again.',
  SOURCE_REQUIRED: 'No file was sent. Go back and choose the file again.',
  MAPPING_INVALID: 'That column mapping is not valid. Go back and check the fields.',
  REQUIRED_FIELD_UNMAPPED: 'A required field is not mapped. Go back and map it.',
  FIELD_NOT_AVAILABLE: 'One of the mapped fields is not available for this account. Go back and change it.',
  MISSING_FIRST_NAME: 'Missing first name',
  MISSING_EMAIL: 'Missing email',
  INVALID_EMAIL: 'Invalid email',
  MISSING_COMPANY_NAME: 'Missing company name',
  VALUE_TOO_LONG: 'Value too long',
  DUPLICATE_IN_FILE: 'Duplicate of another row in this file',
  DUPLICATE_SKIPPED: 'Duplicate of an existing record, skipped per the chosen strategy',
  NO_CHANGES: 'No changes to apply',
  WRITE_FAILED: 'This row could not be saved',
  IMPORT_DISABLED: FORBIDDEN_COPY,
  OWNER_REQUIRED: 'Choose who will own the imported records. Go back to Review and pick an owner.',
  OWNER_INVALID: 'The chosen owner is not an active user of this account. Go back to Review and pick another owner.'
}

function detailOf(extensions?: Record<string, unknown>): Record<string, unknown> {
  const detail = extensions?.detail
  return detail && typeof detail === 'object' ? (detail as Record<string, unknown>) : {}
}

/**
 * Turns an ApolloError from any import operation into one line of user-facing copy.
 *
 * A known code uses {@link IMPORT_ERROR_COPY} and never leaks the server message. An unknown code
 * falls back to the server's own message, and a network or unknown failure to a generic line.
 */
export function getImportErrorMessage(error?: ApolloError | null): string {
  if (!error) {
    return GENERIC_IMPORT_ERROR
  }

  // An ImportError arrives as `code: 'BAD_USER_INPUT'` with the real code on `importCode`
  // (apps/api runImportResolver.ts). FORBIDDEN and other Apollo errors only have `code`.
  const graphQLError = error.graphQLErrors?.find((e) => !!(e?.extensions?.importCode || e?.extensions?.code))
  const code = (graphQLError?.extensions?.importCode ?? graphQLError?.extensions?.code) as string | undefined

  if (code === 'FORBIDDEN') {
    return FORBIDDEN_COPY
  }

  if (code && code in IMPORT_ERROR_COPY) {
    const typedCode = code as ImportErrorCode
    const detail = detailOf(graphQLError?.extensions as Record<string, unknown> | undefined)

    if (typedCode === 'MAPPING_INVALID' && typeof detail.reason === 'string') {
      return `${IMPORT_ERROR_COPY.MAPPING_INVALID} (${detail.reason})`
    }

    return IMPORT_ERROR_COPY[typedCode]
  }

  // Unknown GraphQL error: the server message is the best information available.
  const serverMessage = graphQLError?.message ?? error.graphQLErrors?.[0]?.message
  if (serverMessage) {
    return serverMessage
  }

  return GENERIC_IMPORT_ERROR
}
